/* =====================================================================
 * AdminErrorsPanel — 앱 에러 이벤트 목록 (owner 전용, 에러 탭)
 *
 * error_events(Postgres) 기반. 프록시가 AI 서버에서 받은 비-200 등
 * 사용자 대면 에러를 최신순으로 노출한다. 3개 환경(local/dev/prod) 균일.
 * ===================================================================== */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Loader2, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { listErrorEvents, type ErrorEventItem } from '@auw/api-client';

const PAGE = 100;

function formatTime(iso?: string | null): string {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export const AdminErrorsPanel: React.FC = () => {
  const { t } = useTranslation('admin');
  const addToast = useChatStore((s) => s.addToast);

  const [items, setItems] = useState<ErrorEventItem[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async (nextOffset: number) => {
    setLoading(true);
    try {
      const res = await listErrorEvents({ limit: PAGE, offset: nextOffset });
      setItems((prev) => (nextOffset === 0 ? res.items : [...prev, ...res.items]));
      setOffset(nextOffset);
      setHasMore(res.has_more);
    } catch (e) {
      addToast({ type: 'error', title: t('appManage.errors.loadFail'), description: e instanceof Error ? e.message : undefined });
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  useEffect(() => { void load(0); }, [load]);

  return (
    <>
      <Toolbar>
        <RefreshBtn type="button" onClick={() => void load(0)} disabled={loading} aria-label={t('appManage.refresh')}>
          <RefreshCw size={15} className={loading ? 'spin' : undefined} />
          {t('appManage.refresh')}
        </RefreshBtn>
      </Toolbar>
      {loading && items.length === 0 ? (
        <Center><Loader2 size={18} className="spin" /></Center>
      ) : items.length === 0 ? (
        <Center>{t('appManage.errors.empty')}</Center>
      ) : (
        <>
      <TableWrap>
      <Table>
        <thead>
          <tr>
            <Th>{t('appManage.errors.time')}</Th>
            <Th>{t('appManage.errors.feature')}</Th>
            <Th>{t('appManage.errors.type')}</Th>
            <Th $right>{t('appManage.errors.code')}</Th>
            <Th>{t('appManage.errors.message')}</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {items.map((it) => {
            const isOpen = openId === it.error_id;
            return (
              <React.Fragment key={it.error_id}>
                <tr>
                  <Td>{formatTime(it.created_at)}</Td>
                  <Td>{it.feature ? t(`appManage.features.${it.feature}`, it.feature) : '-'}</Td>
                  <Td>{it.error_type || '-'}</Td>
                  <Td $right>{it.http_status ?? '-'}</Td>
                  <TdMsg>{it.message || '-'}</TdMsg>
                  <Td $right>
                    {it.detail && (
                      <LinkBtn type="button" onClick={() => setOpenId(isOpen ? null : it.error_id)}>
                        {isOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        {isOpen ? t('appManage.errors.hideDetail') : t('appManage.errors.detail')}
                      </LinkBtn>
                    )}
                  </Td>
                </tr>
                {isOpen && it.detail && (
                  <tr>
                    <DetailCell colSpan={6}>
                      <Pre>{it.detail}</Pre>
                      <Meta>
                        {it.source && <span>source: {it.source}</span>}
                        {it.user_id && <span>user: {it.user_id}</span>}
                        {it.path && <span>path: {it.path}</span>}
                        {it.env && <span>env: {it.env}</span>}
                      </Meta>
                    </DetailCell>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      </TableWrap>
      {hasMore && (
        <MoreBtn type="button" disabled={loading} onClick={() => load(offset + PAGE)}>
          {loading ? <Loader2 size={14} className="spin" /> : t('appManage.errors.loadMore')}
        </MoreBtn>
      )}
        </>
      )}
    </>
  );
};

export default AdminErrorsPanel;

const Center = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 14px;
`;

const RefreshBtn = styled.button`
  appearance: none; display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px; border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textPrimary}; font-size: 0.8125rem; cursor: pointer;
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
  &:disabled { opacity: 0.6; cursor: default; }
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
`;

/* 모바일: 컬럼이 많은 에러 표가 카드를 넘쳐 깨지지 않도록 가로 스크롤로 가둔다. */
const TableWrap = styled.div`
  width: 100%;
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    min-width: 640px;
  }
`;

const Th = styled.th<{ $right?: boolean }>`
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 8px 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textMuted};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td<{ $right?: boolean }>`
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  padding: 8px 10px;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  white-space: nowrap;
`;

const TdMsg = styled.td`
  padding: 8px 10px;
  color: ${({ theme }) => theme.colors.textPrimary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LinkBtn = styled.button`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  &:hover { color: ${({ theme }) => theme.colors.textPrimary}; }
`;

const DetailCell = styled.td`
  padding: 0 10px 12px;
  background: ${({ theme }) => theme.colors.bgSecondary};
`;

const Pre = styled.pre`
  margin: 12px 0 8px;
  padding: 12px;
  font-size: 0.75rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${({ theme }) => theme.colors.textPrimary};
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  max-height: 280px;
  overflow: auto;
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MoreBtn = styled.button`
  appearance: none;
  width: 100%;
  margin-top: 12px;
  padding: 10px;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadius};
  cursor: pointer;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  display: flex;
  align-items: center;
  justify-content: center;
  .spin { animation: spin 1s linear infinite; }
  @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
  &:disabled { opacity: 0.6; cursor: default; }
`;
